import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { UNSAFE_DataRouterContext, useBlocker, useNavigate } from 'react-router-dom';
import { Button, Dialog, PatientConfirmLine } from '@etnas/design-system';
import { Modal } from '@/components/Modal';
import { useAuth } from './auth';
import { patientHome } from './workspaces';
import { initialsOf } from '@/lib/patient';
import { useQueryClient } from '@tanstack/react-query';

export type SlotNo = 1 | 2 | 3 | 4;
export interface OpenSlot {
  slot: SlotNo;
  patientId: string;
  /** Legal display name, cached from the last load (kept in memory only) */
  display?: string;
  meta?: string;
}

interface DirtyEntry {
  label: string;
  save?: () => Promise<void>;
  discard?: () => void | Promise<void>;
}

interface SlotsContextValue {
  slots: OpenSlot[];
  slotOf: (patientId: string) => SlotNo | null;
  /** Open (or focus) a patient in a slot and navigate to the role's patient home or `to`. */
  openPatient: (p: { id: string; display?: string; meta?: string }, to?: string) => void;
  closeSlot: (slot: SlotNo) => void;
  describe: (patientId: string, display: string, meta?: string) => void;
  /** Unsaved-work registry (per patient) */
  setDirty: (patientId: string, key: string, entry: DirtyEntry | null) => void;
  dirtyFor: (patientId: string) => DirtyEntry[];
  dirtyPatients: () => string[];
  /** Ask save/discard/stay if `patientId` has unsaved work. Resolves true when it is OK to proceed. */
  confirmLeave: (patientId: string) => Promise<boolean>;
}

const Ctx = createContext<SlotsContextValue | null>(null);
const STORE = 'etnas.slots';

/**
 * Up to four open charts keyed A–D (guideline 10). Only ids are persisted (session storage) so a
 * restored session reopens the same charts; names are reloaded from the API.
 */
export function PatientSlotsProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const navigate = useNavigate();
  const [slots, setSlots] = useState<OpenSlot[]>(() => {
    try {
      const raw = sessionStorage.getItem(STORE);
      const v = raw ? (JSON.parse(raw) as { userId: string; slots: OpenSlot[] }) : null;
      return v && v.userId === me?.user.id ? v.slots.map((s) => ({ slot: s.slot, patientId: s.patientId })) : [];
    } catch {
      return [];
    }
  });
  const [dirty, setDirtyState] = useState<Record<string, Record<string, DirtyEntry>>>({});
  const [ask, setAsk] = useState<null | { patientId: string; resolve: (ok: boolean) => void }>(null);
  const [full, setFull] = useState<null | { p: { id: string; display?: string; meta?: string }; to?: string }>(null);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => {
    if (!me) return;
    try {
      sessionStorage.setItem(STORE, JSON.stringify({ userId: me.user.id, slots: slots.map((s) => ({ slot: s.slot, patientId: s.patientId })) }));
    } catch {
      /* ignore */
    }
  }, [slots, me]);

  // Warn before closing the browser tab with unsaved work.
  useEffect(() => {
    const onBefore = (e: BeforeUnloadEvent) => {
      if (Object.values(dirtyRef.current).some((m) => Object.keys(m).length)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, []);

  const slotOf = useCallback((id: string) => slots.find((s) => s.patientId === id)?.slot ?? null, [slots]);

  const go = useCallback(
    (id: string, to?: string) => {
      const role = me?.context.role.key;
      const dest = to ?? (role ? patientHome(role, id, me?.context.roleId) : null);
      if (dest) navigate(dest);
    },
    [me, navigate],
  );

  const openPatient = useCallback(
    (p: { id: string; display?: string; meta?: string }, to?: string) => {
      setSlots((cur) => {
        if (cur.some((s) => s.patientId === p.id)) return cur.map((s) => (s.patientId === p.id ? { ...s, display: p.display ?? s.display, meta: p.meta ?? s.meta } : s));
        if (cur.length >= 4) {
          setFull({ p, to });
          return cur;
        }
        const used = new Set(cur.map((s) => s.slot));
        const free = ([1, 2, 3, 4] as SlotNo[]).find((n) => !used.has(n))!;
        return [...cur, { slot: free, patientId: p.id, display: p.display, meta: p.meta }].sort((a, b) => a.slot - b.slot);
      });
      if (slots.length < 4 || slots.some((s) => s.patientId === p.id)) go(p.id, to);
    },
    [go, slots],
  );

  const dirtyFor = useCallback((id: string) => Object.values(dirtyRef.current[id] ?? {}), []);
  const dirtyPatients = useCallback(() => Object.keys(dirtyRef.current).filter(id => Object.keys(dirtyRef.current[id]).length > 0), []);

  const confirmLeave = useCallback(
    (patientId: string) =>
      new Promise<boolean>((resolve) => {
        if (!Object.keys(dirtyRef.current[patientId] ?? {}).length) return resolve(true);
        setAsk({ patientId, resolve });
      }),
    [],
  );

  const closeSlot = useCallback(
    async (slot: SlotNo) => {
      const s = slots.find((x) => x.slot === slot);
      if (!s) return;
      if (!(await confirmLeave(s.patientId))) return;
      setSlots((cur) => cur.filter((x) => x.slot !== slot));
    },
    [slots, confirmLeave],
  );

  const describe = useCallback((patientId: string, display: string, meta?: string) => {
    setSlots((cur) => (cur.some((s) => s.patientId === patientId && (s.display !== display || s.meta !== meta)) ? cur.map((s) => (s.patientId === patientId ? { ...s, display, meta } : s)) : cur));
  }, []);

  const setDirty = useCallback((patientId: string, key: string, entry: DirtyEntry | null) => {
    const update = (cur: typeof dirtyRef.current) => {
      const m = { ...(cur[patientId] ?? {}) };
      if (entry) {
        if (m[key]?.label === entry.label && m[key]?.save === entry.save && m[key]?.discard === entry.discard) return cur;
        m[key] = entry;
      } else {
        if (!m[key]) return cur;
        delete m[key];
      }
      return { ...cur, [patientId]: m };
    };
    dirtyRef.current = update(dirtyRef.current);
    setDirtyState(dirtyRef.current);
  }, []);

  const value = useMemo(
    () => ({ slots: slots.map((s) => ({ ...s })), slotOf, openPatient, closeSlot, describe, setDirty, dirtyFor, dirtyPatients, confirmLeave }),
    [slots, slotOf, openPatient, closeSlot, describe, setDirty, dirtyFor, dirtyPatients, confirmLeave],
  );

  const askSlot = ask ? slots.find((s) => s.patientId === ask.patientId) : null;
  return (
    <Ctx.Provider value={value}>
      {children}
      {ask ? (
        <UnsavedDialog
          display={askSlot?.display ?? 'this patient'}
          slot={askSlot?.slot ?? 1}
          entries={Object.values(dirty[ask.patientId] ?? {})}
          onStay={() => {
            ask.resolve(false);
            setAsk(null);
          }}
          onDiscard={async () => {
            for (const e of Object.values(dirtyRef.current[ask.patientId] ?? {})) await e.discard?.();
            dirtyRef.current = { ...dirtyRef.current, [ask.patientId]: {} };
            setDirtyState((cur) => ({ ...cur, [ask.patientId]: {} }));
            ask.resolve(true);
            setAsk(null);
          }}
          onSave={async () => {
            for (const e of Object.values(dirtyRef.current[ask.patientId] ?? {})) {
              if (!e.save) throw new Error('This work must be completed or explicitly discarded.');
              await e.save();
            }
            dirtyRef.current = { ...dirtyRef.current, [ask.patientId]: {} };
            setDirtyState((cur) => ({ ...cur, [ask.patientId]: {} }));
            ask.resolve(true);
            setAsk(null);
          }}
        />
      ) : null}
      {full ? (
        <Modal onClose={() => setFull(null)}>
          <Dialog title="Four charts are open" eyebrow="Patient slots" onClose={() => setFull(null)} width={520} footer={<Button variant="quiet" onClick={() => setFull(null)}>Cancel</Button>}>
            <p style={{ margin: 0 }}>Choose a chart to close so {full.p.display ?? 'the new patient'} can open in its slot.</p>
            <div className="ex-stack is-tight">
              {slots.map((s) => (
                <Button
                  key={s.slot}
                  variant="secondary"
                  icon="x"
                  onClick={async () => {
                    if (!(await confirmLeave(s.patientId))) return;
                    const target = full;
                    setFull(null);
                    setSlots((cur) => [...cur.filter((x) => x.slot !== s.slot), { slot: s.slot, patientId: target.p.id, display: target.p.display, meta: target.p.meta }].sort((a, b) => a.slot - b.slot));
                    go(target.p.id, target.to);
                  }}
                >
                  Close {'ABCD'[s.slot - 1]} · {s.display ?? 'patient'}
                </Button>
              ))}
            </div>
          </Dialog>
        </Modal>
      ) : null}
    </Ctx.Provider>
  );
}

function UnsavedDialog({ display, slot, entries, onSave, onDiscard, onStay }: { display: string; slot: SlotNo; entries: DirtyEntry[]; onSave: () => Promise<void>; onDiscard: () => Promise<void>; onStay: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSave = entries.length > 0 && entries.every((e) => e.save);
  const run = async (action: () => Promise<void>) => {
    setBusy(true); setError(null);
    try { await action(); } catch (e) { setError(e instanceof Error ? e.message : 'The draft could not be saved. Retry or stay here.'); } finally { setBusy(false); }
  };
  return (
    <Modal onClose={onStay} top>
      <Dialog
        title={`Unsaved work for ${display}`}
        eyebrow="Before you switch"
        onClose={onStay}
        width={520}
        footer={
          <>
            <Button variant="quiet" onClick={onStay} disabled={busy} data-autofocus>
              Stay with {display.split(',')[0]}
            </Button>
            <span className="ex-row-gap">
              <Button variant="danger" onClick={() => void run(onDiscard)} disabled={busy}>
                Discard changes
              </Button>
              {canSave ? (
                <Button
                  variant="primary"
                  loading={busy}
                  onClick={() => void run(onSave)}
                >
                  Save and continue
                </Button>
              ) : null}
            </span>
          </>
        }
      >
        <PatientConfirmLine patient={{ initials: initialsOf(display), legalName: display, dob: '', age: '', mrn: '' }} slot={slot} />
        <p style={{ margin: 0 }}>Work never follows you to another patient. Choose what happens to:</p>
        {error ? <p role="alert">{error} Your draft is still here. Retry Save and continue, or stay.</p> : null}
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {entries.map((e, i) => (
            <li key={i}>{e.label}</li>
          ))}
        </ul>
      </Dialog>
    </Modal>
  );
}

export function usePatientSlots(): SlotsContextValue {
  const c = useContext(Ctx);
  if (!c) throw new Error('usePatientSlots outside PatientSlotsProvider');
  return c;
}

/** One router guard per application branch, including section changes and pre-registration drafts. */
export function UnsavedNavigationGuard() {
  const router = useContext(UNSAFE_DataRouterContext);
  return router ? <ActiveNavigationGuard /> : null;
}
function ActiveNavigationGuard() {
  const { dirtyPatients, confirmLeave } = usePatientSlots();
  const handling = useRef(false);
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    currentLocation.pathname + currentLocation.search !== nextLocation.pathname + nextLocation.search && dirtyPatients().length > 0);
  useEffect(() => {
    if (blocker.state !== 'blocked' || handling.current) return;
    handling.current = true;
    void (async () => {
      for (const id of dirtyPatients()) if (!(await confirmLeave(id))) { blocker.reset(); handling.current = false; return; }
      blocker.proceed(); handling.current = false;
    })();
  }, [blocker, confirmLeave, dirtyPatients]);
  return null;
}

/**
 * Register unsaved work for a patient so switching patients asks save/discard/stay.
 * Pass `dirty=false` once saved. `save` (optional) lets "Save and continue" persist it.
 */
export function useUnsavedWork(patientId: string | undefined, key: string, dirty: boolean, label: string, save?: () => Promise<void>, discard?: () => void | Promise<void>) {
  const { setDirty } = usePatientSlots();
  const saveRef = useRef(save);
  const discardRef = useRef(discard);
  discardRef.current = discard;
  saveRef.current = save;
  const stableSave = useMemo(() => (save ? () => saveRef.current!() : undefined), [!!save]); // eslint-disable-line react-hooks/exhaustive-deps
  const stableDiscard = useMemo(() => (discard ? () => discardRef.current!() : undefined), [!!discard]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!patientId) return;
    setDirty(patientId, key, dirty ? { label, save: stableSave, discard: stableDiscard } : null);
    return () => setDirty(patientId, key, null);
  }, [patientId, key, dirty, label, stableSave, stableDiscard, setDirty]);
}

/** Session-memory drafts are keyed by workflow and subject; identity changes clear the query cache. */
export function useWorkflowDraft<T>(subjectId: string, key: string, label: string, value: T, empty: T, restore: (draft: T) => void) {
  const qc = useQueryClient(); const { setDirty } = usePatientSlots();
  const cacheKey = ['draft', subjectId, key];
  const saved = useRef(JSON.stringify(qc.getQueryData<T>(cacheKey) ?? empty));
  const restoreRef = useRef(restore); restoreRef.current = restore;
  const [, changed] = useState(0);
  useEffect(() => { const cached = qc.getQueryData<T>(['draft', subjectId, key]); if (cached) restoreRef.current(cached); }, [qc, subjectId, key]);
  const clear = () => {
    qc.removeQueries({ queryKey: cacheKey, exact: true }); saved.current = JSON.stringify(empty);
    setDirty(subjectId, key, null); restoreRef.current(empty); changed(n => n + 1);
  };
  useUnsavedWork(subjectId, key, JSON.stringify(value) !== saved.current, label,
    async () => { qc.setQueryDefaults(['draft'], { gcTime: Infinity }); qc.setQueryData(cacheKey, value); saved.current = JSON.stringify(value); changed(n => n + 1); }, clear);
  return clear;
}
